import { useEffect, useState } from "react";
import { Check, Lock, Mail, Infinity as InfinityIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PLAN_FEATURES, LOCKED_FOR } from "@/data/planFeatures";
import { STRIPE_TIERS } from "@/lib/stripe-config";

export default function SettingsPage() {
  const { user } = useAuth();
  const { plan, subscribed, subscriptionEnd, analysesUsed, analysesLimit, refresh } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageMeta({ title: "Settings — Unranked" });

  useEffect(() => {
    if (searchParams.get("upgrade") === "success") {
      refresh();
      toast({ title: "Plan upgraded", description: "Welcome to Growth!" });
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Profile: email
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // Profile: password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) return;
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      toast({ title: "Confirmation email sent", description: "Check your email for a confirmation link to finish changing your address. Your current email will stay the same until you confirm." });
      setNewEmail("");
    } catch (e: any) {
      const msg = e.message || "";
      const friendly = msg.includes("already been registered")
        ? "This email is already in use by another account."
        : msg.includes("Unable to validate email")
        ? "Please enter a valid email address."
        : msg || "Could not update email.";
      toast({ title: "Error", description: friendly, variant: "destructive" });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword.length < 6) {
      toast({ title: "Too short", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: "Password updated", description: "Your password has been changed." });
      setNewPassword("");
      setConfirmPassword("");
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Could not update password.", variant: "destructive" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast({ title: "Error", description: "Could not open subscription management.", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgradeToGrowth = async () => {
    setUpgradeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { flow: "subscription_update", price_id: STRIPE_TIERS.growth.price_id },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast({ title: "Error", description: "Could not start upgrade.", variant: "destructive" });
    } finally {
      setUpgradeLoading(false);
    }
  };

  const planLabel = plan === "growth" ? "Growth" : plan === "starter" ? "Starter" : "Free";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-display">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account</p>
      </div>

      {/* Profile */}
      <div className="rounded-lg border border-border bg-card p-8 space-y-6">
        <h2 className="text-lg font-semibold text-foreground">Profile</h2>

        <div className="space-y-1">
          <Label className="text-muted-foreground">Current email</Label>
          <p className="text-foreground">{user?.email}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-email">New email address</Label>
          <p className="text-xs text-muted-foreground">You'll receive a confirmation link at your new address. Your email won't change until you confirm.</p>
          <div className="flex gap-3 items-center">
            <Input
              id="new-email"
              type="email"
              placeholder="new@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="max-w-sm"
            />
            <Button variant="outline" onClick={handleUpdateEmail} disabled={emailLoading || !newEmail.trim()}>
              {emailLoading ? "Sending…" : "Update Email"}
            </Button>
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            placeholder="Min. 6 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="max-w-sm"
          />
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="max-w-sm"
          />
          <Button variant="outline" onClick={handleUpdatePassword} disabled={passwordLoading || !newPassword}>
            {passwordLoading ? "Updating…" : "Update Password"}
          </Button>
        </div>
      </div>

      {/* Manage Plan */}
      <div className="rounded-lg border border-border bg-card p-8 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Manage Plan</h2>

        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-foreground">{planLabel}</span>
          <Badge variant={subscribed ? "default" : "secondary"}>
            {subscribed ? "Active" : "Free"}
          </Badge>
        </div>

        {subscriptionEnd && (
          <div>
            <p className="text-sm text-muted-foreground">Renews on</p>
            <p className="text-foreground">{new Date(subscriptionEnd).toLocaleDateString()}</p>
          </div>
        )}

        {/* Usage */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Analyses this period</p>
          {analysesLimit === null ? (
            <>
              <div className="flex items-center gap-2">
                <InfinityIcon className="w-6 h-6 text-primary" />
                <span className="text-foreground font-semibold text-lg">Unlimited</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {analysesUsed} {analysesUsed === 1 ? "analysis" : "analyses"} run this billing period
              </p>
            </>
          ) : (
            <>
              <p className="text-foreground font-medium">
                {analysesUsed} / {analysesLimit} used
              </p>
              <Progress value={(analysesUsed / analysesLimit) * 100} className="h-2 max-w-xs" />
              <p className="text-xs text-muted-foreground">
                {Math.max(0, analysesLimit - analysesUsed)} remaining
                {plan === "free" ? " (no monthly renewal)" : ""}
              </p>
            </>
          )}
        </div>

        {/* What's included */}
        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            What's included in {planLabel}
          </h3>
          <ul className="space-y-2">
            {PLAN_FEATURES[plan].map((feat) => (
              <li key={feat} className="flex items-center gap-2 text-sm text-foreground">
                <Check className="w-4 h-4 text-primary shrink-0" />
                <span>{feat}</span>
              </li>
            ))}
            {LOCKED_FOR[plan].map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-2 text-sm text-muted-foreground/70"
              >
                <Lock className="w-4 h-4 shrink-0" />
                <span>
                  {item.label}{" "}
                  <span className="text-xs">
                    — {item.requires === "starter" ? "Starter" : "Growth"} only
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          {plan === "starter" && (
            <Button
              onClick={handleUpgradeToGrowth}
              disabled={upgradeLoading}
              className="glow-primary"
            >
              {upgradeLoading ? "Loading…" : "Upgrade to Growth"}
            </Button>
          )}
          {subscribed && (
            <Button variant="outline" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? "Loading…" : "Manage Subscription"}
            </Button>
          )}
          {!subscribed && (
            <Button onClick={() => navigate("/pricing")} className="glow-primary">
              Upgrade Plan
            </Button>
          )}
        </div>
      </div>

      {/* Support */}
      <div className="rounded-lg border border-border bg-card p-8 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Need help?</h2>
        <p className="text-sm text-muted-foreground">
          Questions about your account, billing, or analysis? We're here to help.
        </p>
        <Button variant="outline" asChild>
          <a href="mailto:hello@unrankedagency.com">
            <Mail className="w-4 h-4 mr-2" />
            Contact Support
          </a>
        </Button>
      </div>
    </div>
  );
}
